<template>
  <div
    :class="DS_CARD_CLASSES.Root"
    :aria-label="props.ariaLabel"
    data-cy="ds-card"
  >
    <header
      v-if="hasHeader"
      :class="DS_CARD_CLASSES.Header"
      data-cy="ds-card__header"
    >
      <span
        v-if="props.icon"
        :class="DS_CARD_CLASSES.Icon"
        data-cy="ds-card__icon"
      >
        <DSIcon
          :icon="props.icon"
          size="sm"
          aria-hidden="true"
        />
      </span>

      <div :class="DS_CARD_CLASSES.Heading">
        <div
          :id="titleId"
          :class="DS_CARD_CLASSES.Title"
          data-cy="ds-card__title"
        >
          {{ props.header }}
        </div>
        <div
          v-if="hasSubtitle"
          :class="DS_CARD_CLASSES.Subtitle"
          data-cy="ds-card__subtitle"
        >
          {{ props.subtitle }}
        </div>

        <div
          v-if="hasSuffix"
          :class="DS_CARD_CLASSES.Suffix"
          data-cy="ds-card__suffix"
        >
          <slot name="suffix" />
        </div>
      </div>

      <div
        v-if="hasActions"
        :class="DS_CARD_CLASSES.Actions"
        data-cy="ds-card__actions"
      >
        <slot name="actions" />
      </div>

      <DSButton
        v-if="isCollapsible"
        :class="DS_CARD_CLASSES.Toggle"
        variant="ghost"
        size="sm"
        :prefix-icon="expanded ? 'chevron-top' : 'chevron-down'"
        :aria-labelledby="titleId"
        :aria-expanded="expanded"
        :aria-controls="contentId"
        data-cy="ds-card__toggle"
        @click="toggleExpanded"
        @dblclick.stop
      />
    </header>

    <div
      :id="contentId"
      :class="DS_CARD_CLASSES.Content"
      :hidden="!expanded || !hasSlotContent(slots)"
      data-cy="ds-card__content"
    >
      <slot />
    </div>
  </div>
</template>

<script setup lang="ts">
import type { DSCardProps } from "@/src/design_system/layouts_and_containers/card/types/props";
import { DS_CARD_CLASSES } from "@/src/design_system/layouts_and_containers/card/constants/classes";
import DSIcon from "@/src/design_system/primitives/icon/components/DSIcon.vue";
import { computed, useSlots } from "vue";
import { usePersistentPreference } from "@/src/design_system/shared/composables/usePersistentPreference";
import { getPersistenceKey } from "@/src/design_system/shared/utils/persistenceKey";
import { DS_CARD_EXPANDED_STORAGE_KEY } from "@/src/design_system/layouts_and_containers/card/constants/persistence";
import { generateUUID } from "@/src/shared/utils/uuid";
import { hasSlotContent } from "@/src/shared/utils/slots";
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";

const props = withDefaults(defineProps<DSCardProps>(), {
  header: undefined,
  subtitle: undefined,
  icon: undefined,
  ariaLabel: undefined,
  collapsible: false,
  persistenceKey: undefined,
});

const slots = useSlots();

const expandedModel = defineModel<boolean>("expanded", { default: true });

const expanded = usePersistentPreference({
  storageKey: DS_CARD_EXPANDED_STORAGE_KEY,
  key: getPersistenceKey(props.persistenceKey),
  value: expandedModel,
  validate: (value): value is boolean => typeof value === "boolean",
});

const toggleExpanded = () => {
  expanded.value = !expanded.value;
};

const instanceId = generateUUID();
const titleId = `ds-card-title-${instanceId}`;
const contentId = `ds-card-content-${instanceId}`;

const hasHeader = computed(() => Boolean(props.header?.trim()));
const hasSubtitle = computed(() => Boolean(props.subtitle?.trim()));
const hasSuffix = computed(() => hasSlotContent(slots, "suffix"));
const hasActions = computed(() => hasSlotContent(slots, "actions"));
const isCollapsible = computed(() => props.collapsible && hasHeader.value);
</script>
